import { toast } from 'react-toastify';
import { axiosInstance } from '../../repository/Repository';
import {
    CANCEL_STRIPE_DOWNGRADE_PENDING,
    CANCEL_STRIPE_DOWNGRADE_REJECTED,
    CANCEL_STRIPE_DOWNGRADE_SUCCESS,
    CANCEL_STRIPE_SUBSCRIPTION_PENDING,
    CANCEL_STRIPE_SUBSCRIPTION_REJECTED,
    CANCEL_STRIPE_SUBSCRIPTION_SUCCESS,
    CREATE_STRIPE_CHECKOUT_SESSION_PENDING,
    CREATE_STRIPE_CHECKOUT_SESSION_REJECTED,
    CREATE_STRIPE_CHECKOUT_SESSION_SUCCESS,
    CREATE_STRIPE_CHECKOUT_SESSION_RESET,
    GET_STRIPE_SUBSCRIPTION_STATUS_PENDING,
    GET_STRIPE_SUBSCRIPTION_STATUS_REJECTED,
    GET_STRIPE_SUBSCRIPTION_STATUS_SUCCESS,
    PREVIEW_STRIPE_PLAN_CHANGE_PENDING,
    PREVIEW_STRIPE_PLAN_CHANGE_REJECTED,
    PREVIEW_STRIPE_PLAN_CHANGE_RESET,
    PREVIEW_STRIPE_PLAN_CHANGE_SUCCESS,
    RESUME_STRIPE_SUBSCRIPTION_PENDING,
    RESUME_STRIPE_SUBSCRIPTION_REJECTED,
    RESUME_STRIPE_SUBSCRIPTION_SUCCESS,
} from './types';
const subscriptionStatusRequests = new Map();

export const fetchStripeSubscriptionStatus = (email) => async (dispatch) => {
    if (!email) {
        return null;
    }

    if (subscriptionStatusRequests.has(email)) {
        return subscriptionStatusRequests.get(email);
    }

    dispatch({ type: GET_STRIPE_SUBSCRIPTION_STATUS_PENDING });
    const request = axiosInstance
        .post('stripe/subscription-status', {
            email,
        })
        .then((response) => {
            dispatch({
                type: GET_STRIPE_SUBSCRIPTION_STATUS_SUCCESS,
                payload: response.data || null,
            });
            return response.data || null;
        })
        .catch((error) => {
            dispatch({ type: GET_STRIPE_SUBSCRIPTION_STATUS_REJECTED });
            toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch subscription status');
            throw error;
        })
        .finally(() => {
            subscriptionStatusRequests.delete(email);
        });

    subscriptionStatusRequests.set(email, request);
    return request;
};

export const createStripeCheckoutSession =
    (data, onSuccess = () => {}, onError = () => {}) =>
    async (dispatch) => {
        dispatch({
            type: CREATE_STRIPE_CHECKOUT_SESSION_PENDING,
            payload: data?.priceId || null,
        });
        try {
            const response = await axiosInstance.post(
                'stripe/create-checkout-session',
                data
            );
            dispatch({
                type: CREATE_STRIPE_CHECKOUT_SESSION_SUCCESS,
                payload: response.data,
            });
            dispatch({ type: CREATE_STRIPE_CHECKOUT_SESSION_RESET });
            onSuccess(response.data);
            return response.data;
        } catch (error) {
            dispatch({ type: CREATE_STRIPE_CHECKOUT_SESSION_REJECTED });
            dispatch({ type: CREATE_STRIPE_CHECKOUT_SESSION_RESET });
            toast.error(
                error?.response?.data?.message || 'Unable create checkout session'
            );
            onError(error);
            throw error;
        }
    };

export const previewStripePlanChange =
    ({ email, priceId }) =>
    async (dispatch) => {
        dispatch({ type: PREVIEW_STRIPE_PLAN_CHANGE_PENDING });
        try {
            const response = await axiosInstance.get(
                'stripe/preview-plan-change',
                {
                    params: { email, priceId },
                }
            );
            dispatch({
                type: PREVIEW_STRIPE_PLAN_CHANGE_SUCCESS,
                payload: response.data || null,
            });
            return response.data || null;
        } catch (error) {
            dispatch({ type: PREVIEW_STRIPE_PLAN_CHANGE_REJECTED });
            toast.error(
                error?.response?.data?.message ||
                    'Unable to preview plan change'
            );
            throw error;
        }
    };

export const resetPreviewStripePlanChange = () => (dispatch) => {
    dispatch({ type: PREVIEW_STRIPE_PLAN_CHANGE_RESET });
};

export const resumeStripeSubscription = (email) => async (dispatch) => {
    dispatch({ type: RESUME_STRIPE_SUBSCRIPTION_PENDING });
    try {
        const response = await axiosInstance.post('stripe/resume-subscription', {
            user: { email },
        });
        dispatch({ type: RESUME_STRIPE_SUBSCRIPTION_SUCCESS });
        await dispatch(fetchStripeSubscriptionStatus(email));
        return response.data;
    } catch (error) {
        dispatch({ type: RESUME_STRIPE_SUBSCRIPTION_REJECTED });
        throw error;
    }
};

export const cancelStripeDowngrade = (email) => async (dispatch) => {
    dispatch({ type: CANCEL_STRIPE_DOWNGRADE_PENDING });
    try {
        const response = await axiosInstance.post('stripe/cancel-downgrade', {
            user: { email },
        });
        dispatch({ type: CANCEL_STRIPE_DOWNGRADE_SUCCESS });
        await dispatch(fetchStripeSubscriptionStatus(email));
        return response.data;
    } catch (error) {
        dispatch({ type: CANCEL_STRIPE_DOWNGRADE_REJECTED });
        throw error;
    }
};

export const cancelStripeSubscription = (email) => async (dispatch) => {
    dispatch({ type: CANCEL_STRIPE_SUBSCRIPTION_PENDING });
    try {
        const response = await axiosInstance.post('stripe/unsubscribe', {
            user: { email },
        });
        dispatch({ type: CANCEL_STRIPE_SUBSCRIPTION_SUCCESS });
        await dispatch(fetchStripeSubscriptionStatus(email));
        return response.data;
    } catch (error) {
        dispatch({ type: CANCEL_STRIPE_SUBSCRIPTION_REJECTED });
        throw error;
    }
};

export const changePaymentMethod = (paymentMethodId, onSuccess = () => {}, onError = () => {}) => async (dispatch) => {
    try {
        const response = await axiosInstance.post('/stripe/change-payment-method', {
            paymentMethodId,
        });

        if (response.data && (response.status === 200 || response.status === 201)) {
            toast.success('Card updated successfully!');
            onSuccess(response.data.data);
        } else {
            toast.error('Failed to update card details');
            onError();
        }
    } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || 'An error occurred while updating card');
        onError();
    }
};

export const connectStripeAccount = (setConnectLoading = () => {}) => async (dispatch) => {
    try {
        setConnectLoading(true);
        const response = await axiosInstance.post('stripe/accounts');
        const redirectUrl = response.data.url || response.data.accountLink?.url;
        if (redirectUrl) {
            window.location = redirectUrl;
            setConnectLoading(false);
        } else {
            toast.error('Unable to retrieve connect account URL');
            setConnectLoading(false);
        }
    } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || 'Unable to connect');
        console.error(
            'Failed to create account:',
            error?.response?.data?.message || error?.message
        );
        setConnectLoading(false);
    }
};

export const disconnectStripeAccount = (onSuccess = () => {}) => async (dispatch) => {
    try {
        const response = await axiosInstance.delete('stripe/remove');
        if (response.data?.success || response.status === 200 || response.status === 201) {
            toast.success(response.data?.message || 'Stripe account disconnected successfully');
            onSuccess();
        } else {
            toast.error(response.data?.message || 'Failed to disconnect Stripe account');
        }
    } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || 'Failed to disconnect Stripe account');
        console.error('Failed to disconnect Stripe account:', error);
    }
};
